import * as React from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy MultiSelect
 *
 * Searchable multi-select dropdown with chip/tag display for selected items.
 * Gold focus ring, warm borders, keyboard navigation, and animation.
 *
 * Features:
 *   - Search/filter options in dropdown
 *   - Selected items shown as removable chips in trigger
 *   - Backspace removes last selected chip
 *   - Clear all button
 *   - Keyboard navigation (ArrowUp/Down, Enter, Escape, Home/End)
 *   - Option groups
 *   - Disabled options and max selection limit
 *   - "Select all" action in dropdown
 *   - Accessible: role="combobox", aria-expanded, aria-selected
 *
 * Usage:
 *   <MultiSelect
 *     label="Select Staff"
 *     options={[
 *       { value: '1', label: 'Dr. Smith (jsmith@jpgacademy.org)' },
 *       { value: '2', label: 'Mrs. Johnson (mjohnson@jpgacademy.org)' },
 *     ]}
 *     value={selected}
 *     onChange={setSelected}
 *     placeholder="Choose staff members…"
 *     searchable
 *   />
 */

export interface MultiSelectOption {
	value: string
	label: string
	/** Optional group label for optgroups */
	group?: string
	/** Disable this option */
	disabled?: boolean
}

export interface MultiSelectProps {
	/** Label text */
	label?: string
	/** Helper text */
	hint?: string
	/** Error message */
	error?: string
	/** Available options */
	options: MultiSelectOption[]
	/** Selected values */
	value?: string[]
	/** Change handler */
	onChange?: (value: string[]) => void
	/** Placeholder text */
	placeholder?: string
	/** Enable type-to-filter search */
	searchable?: boolean
	/** Disable the select */
	disabled?: boolean
	/** Input size */
	size?: 'sm' | 'md' | 'lg'
	/** Maximum number of selections allowed */
	maxSelections?: number
	/** Message shown when no results match search */
	emptyMessage?: string
	/** Show a "Select all" action at the top of the dropdown */
	selectAll?: boolean
	/** Additional class */
	className?: string
	/** ID override */
	id?: string
}

/**
 * Strip diacritical marks and lowercase for accent-insensitive matching.
 * "Théaux" → "theaux", "José" → "jose", "Ñoño" → "nono"
 */
function normalizeForSearch(s: string): string {
	return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

const sizeStyles = {
	sm: 'min-h-[32px] text-xs px-2.5 py-1',
	md: 'min-h-[40px] text-sm px-3 py-1.5',
	lg: 'min-h-[48px] text-base px-4 py-2',
}

const chipSizeStyles = {
	sm: 'h-5 text-[10px] gap-0.5 px-1.5',
	md: 'h-6 text-xs gap-1 px-2',
	lg: 'h-7 text-sm gap-1 px-2.5',
}

const chipRemoveSize = {
	sm: 'w-3 h-3',
	md: 'w-3.5 h-3.5',
	lg: 'w-4 h-4',
}

function HighlightedText({ text, query }: { text: string; query: string }) {
	if (!query) return <>{text}</>
	const normalizedText = normalizeForSearch(text)
	const normalizedQuery = normalizeForSearch(query)
	const idx = normalizedText.indexOf(normalizedQuery)
	if (idx === -1) return <>{text}</>

	return (
		<>
			{text.slice(0, idx)}
			<span className="font-semibold bg-gold/20 rounded px-0.5">{text.slice(idx, idx + query.length)}</span>
			{text.slice(idx + query.length)}
		</>
	)
}

function CheckIcon({ size }: { size: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
			<polyline points="20 6 9 17 4 12" />
		</svg>
	)
}

function ChipRemoveIcon({ size }: { size: string }) {
	return (
		<svg className={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	)
}

export function MultiSelect({
	label,
	hint,
	error,
	options,
	value = [],
	onChange,
	placeholder = 'Select…',
	searchable = true,
	disabled = false,
	size = 'md',
	maxSelections,
	emptyMessage = 'No results found',
	selectAll = false,
	className,
	id,
}: MultiSelectProps) {
	const selectId = id || label?.toLowerCase().replace(/\s+/g, '-')
	const labelId = `${selectId}-label`
	const [isOpen, setIsOpen] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const [activeIndex, setActiveIndex] = React.useState(-1)
	const containerRef = React.useRef<HTMLDivElement>(null)
	const triggerRef = React.useRef<HTMLDivElement>(null)
	const dropdownRef = React.useRef<HTMLDivElement>(null)
	const searchInputRef = React.useRef<HTMLInputElement>(null)
	const listboxId = `${selectId}-listbox`
	const selectedSet = React.useMemo(() => new Set(value), [value])

	// Position the portal dropdown relative to the trigger
	const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({})

	React.useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current) return
		const rect = triggerRef.current.getBoundingClientRect()
		const dropdownHeight = 240 // max-h-60 = 15rem = 240px
		const spaceBelow = window.innerHeight - rect.bottom
		const spaceAbove = rect.top
		const dropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

		setDropdownStyle({
			position: 'fixed',
			top: dropUp ? rect.top - Math.min(dropdownHeight, spaceAbove - 8) : rect.bottom + 4,
			left: rect.left,
			width: rect.width,
		 zIndex: 9999,
		})
	}, [isOpen, value.length]) // re-measure when selection changes (chips resize trigger)

	// Close on outside click (accounts for portaled dropdown)
	React.useEffect(() => {
		function handleClick(e: MouseEvent) {
			const target = e.target as Node
			const clickedInside =
				(containerRef.current && containerRef.current.contains(target)) ||
				(dropdownRef.current && dropdownRef.current.contains(target))
			if (!clickedInside) {
				setIsOpen(false)
				setSearch('')
				setActiveIndex(-1)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	// Focus search input when opened
	React.useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen])

	// Close on Escape
	React.useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearch('')
				setActiveIndex(-1)
			}
		}
		if (isOpen) {
			document.addEventListener('keydown', handleKey)
			return () => document.removeEventListener('keydown', handleKey)
		}
	}, [isOpen])

	const filteredOptions = React.useMemo(() => {
		if (searchable && search) {
			return options.filter(o => normalizeForSearch(o.label).includes(normalizeForSearch(search)))
		}
		return options
	}, [options, search, searchable])

	// Group options
	const groups = React.useMemo(() => {
		const grouped = new Map<string, MultiSelectOption[]>()
		for (const opt of filteredOptions) {
			const key = opt.group || ''
			if (!grouped.has(key)) grouped.set(key, [])
			grouped.get(key)!.push(opt)
		}
		return grouped
	}, [filteredOptions])

	// Build flat navigable list (only non-disabled for keyboard nav)
	const navigableOptions = React.useMemo(() => filteredOptions.filter(o => !o.disabled), [filteredOptions])

	// Scroll active item into view on keyboard nav
	const optionElements = React.useRef<(HTMLLIElement | null)[]>([])
	React.useEffect(() => {
		const el = optionElements.current[activeIndex]
		if (el) el.scrollIntoView({ block: 'nearest' })
	}, [activeIndex])

	const isMaxed = maxSelections !== undefined && value.length >= maxSelections

	const handleSelect = (opt: MultiSelectOption) => {
		if (opt.disabled) return
		if (isMaxed && !selectedSet.has(opt.value)) return

		const next = new Set(selectedSet)
		if (next.has(opt.value)) {
			next.delete(opt.value)
		} else {
			next.add(opt.value)
		}
		onChange?.(Array.from(next))
	}

	const handleClearAll = () => {
		onChange?.([])
		setSearch('')
	}

	const handleRemoveChip = (val: string, e?: React.MouseEvent) => {
		if (e) e.stopPropagation()
		onChange?.(value.filter(v => v !== val))
	}

	const handleSelectAll = () => {
		if (isMaxed) return
		const allValues = navigableOptions.map(o => o.value)
		if (value.length === navigableOptions.length) {
			// All selected — deselect all
			onChange?.([])
		} else {
			onChange?.(allValues)
		}
	}

	// Get option label by value
	const getOptionLabel = (val: string) => {
		const opt = options.find(o => o.value === val)
		return opt?.label || val
	}

	const handleTriggerClick = () => {
		if (disabled) return
		setIsOpen(prev => !prev)
		if (isOpen) {
			setSearch('')
			setActiveIndex(-1)
		}
	}

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return

		if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
			e.preventDefault()
			if (!isOpen) {
				setIsOpen(true)
				setActiveIndex(-1)
			}
		}
		if (e.key === 'Backspace' && !isOpen && value.length > 0 && search === '') {
			onChange?.(value.slice(0, -1))
		}
		if (e.key === 'Tab') {
			setIsOpen(false)
			setSearch('')
		}
	}

	const handleSearchKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Backspace' && search === '' && value.length > 0) {
			onChange?.(value.slice(0, -1))
			return
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setActiveIndex(prev => Math.min(prev + 1, navigableOptions.length - 1))
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			setActiveIndex(prev => Math.max(prev - 1, 0))
		}
		if (e.key === 'Home') {
			e.preventDefault()
			setActiveIndex(0)
		}
		if (e.key === 'End') {
			e.preventDefault()
			setActiveIndex(navigableOptions.length - 1)
		}
		if (e.key === 'Enter') {
			e.preventDefault()
			if (activeIndex >= 0 && navigableOptions[activeIndex]) {
				handleSelect(navigableOptions[activeIndex])
			}
		}
		if (e.key === ' ') {
			// Prevent space from scrolling the page when dropdown is open
			e.preventDefault()
			if (activeIndex >= 0 && navigableOptions[activeIndex]) {
				handleSelect(navigableOptions[activeIndex])
			}
		}
	}

	return (
		<div className="space-y-1.5" ref={containerRef}>
			{label && (
				<label
					id={labelId}
					htmlFor={selectId}
					className="block text-sm font-medium text-foreground font-ui"
				>
					{label}
				</label>
			)}

			<div className="relative">
				{/* Trigger area */}
				<div
					ref={triggerRef}
					id={selectId}
					role="combobox"
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-controls={isOpen ? listboxId : undefined}
					aria-labelledby={label ? labelId : undefined}
					onClick={handleTriggerClick}
					onKeyDown={handleTriggerKeyDown}
					tabIndex={disabled ? -1 : 0}
					className={cn(
						'w-full font-ui rounded-md border transition-all duration-fast ease-default',
						'bg-background text-foreground',
						'disabled:cursor-not-allowed disabled:opacity-50',
						'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
						'cursor-text',
						sizeStyles[size],
						'flex items-center gap-1.5 flex-wrap',
						'pr-8',
						error
							? 'border-error/60 focus:ring-error/30 focus:border-error'
							: isOpen
								? 'border-gold'
								: 'border-input hover:border-navy-300',
						disabled && 'cursor-not-allowed',
						className,
					)}
				>
					{/* Chips */}
					<AnimatePresence mode="popLayout">
						{value.map(val => (
							<motion.span
								key={val}
								layout
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.8 }}
								transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
								className={cn(
									'inline-flex items-center rounded-md bg-navy/10 text-navy-900 font-medium',
									'border border-navy/15',
									chipSizeStyles[size],
								)}
							>
								<span className="truncate max-w-[140px]">{getOptionLabel(val)}</span>
								<button
									type="button"
									onClick={e => handleRemoveChip(val, e)}
									className={cn(
										'inline-flex items-center justify-center rounded-sm',
										'text-navy/50 hover:text-navy hover:bg-navy/15 transition-colors',
										'flex-shrink-0',
									)}
									aria-label={`Remove ${getOptionLabel(val)}`}
									disabled={disabled}
									tabIndex={-1}
								>
									<ChipRemoveIcon size={chipRemoveSize[size]} />
								</button>
							</motion.span>
						))}
					</AnimatePresence>

					{/* Search input (inline) */}
					{isOpen && searchable && !isMaxed && (
						<input
							ref={searchInputRef}
							type="text"
							className={cn(
								'flex-1 min-w-[80px] bg-transparent border-none outline-none',
								'font-ui text-sm text-foreground placeholder:text-muted-foreground',
								'p-0 h-auto',
							)}
							placeholder={value.length === 0 ? placeholder : 'Search…'}
							value={search}
							onChange={e => {
								setSearch(e.target.value)
								setActiveIndex(-1)
							}}
							onClick={e => e.stopPropagation()}
							onKeyDown={handleSearchKeyDown}
							disabled={disabled}
						/>
					)}

					{/* Placeholder when no selection and not open */}
					{value.length === 0 && !isOpen && (
						<span className="text-muted-foreground select-none">{placeholder}</span>
					)}

					{/* Maxed indicator */}
					{isMaxed && !isOpen && (
						<span className="text-xs text-muted-foreground ml-auto mr-2">
							{value.length}/{maxSelections}
						</span>
					)}

					{/* Chevron */}
					<motion.span
						className={cn(
							'absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground flex-shrink-0',
						)}
						animate={{ rotate: isOpen ? 180 : 0 }}
						transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
						aria-hidden="true"
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="m6 9 6 6 6-6" />
						</svg>
					</motion.span>
				</div>

				{/* Clear all button (when has selection and not open) */}
				{value.length > 0 && !isOpen && !disabled && (
					<button
						type="button"
						onClick={e => {
							e.stopPropagation()
							handleClearAll()
						}}
						className={cn(
							'absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-sm',
							'text-muted-foreground hover:text-foreground transition-colors',
						)}
						aria-label="Clear all selections"
					>
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<circle cx="12" cy="12" r="10" />
							<path d="m15 9-6 6" />
							<path d="m9 9 6 6" />
						</svg>
					</button>
				)}

				{/* Dropdown — portaled to body to escape overflow containers */}
				{isOpen && createPortal(
					<AnimatePresence>
						<motion.div
							ref={dropdownRef}
							className={cn(
								'bg-card border border-border rounded-lg',
								'shadow-lg',
								'overflow-hidden',
								'max-h-60 overflow-y-auto scrollbar-warm',
							)}
							role="listbox"
							id={listboxId}
							aria-multiselectable="true"
							style={dropdownStyle}
							initial={{ opacity: 0, y: -4, scale: 0.98 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: -4, scale: 0.98 }}
							transition={{ duration: 0.15, ease: [0, 0, 0.2, 1] }}
						>
							{/* Select all action */}
							{selectAll && navigableOptions.length > 0 && (
								<li
									role="option"
									aria-selected={value.length === navigableOptions.length}
									onClick={handleSelectAll}
									onMouseEnter={() => setActiveIndex(-1)}
									className={cn(
										'px-3 py-2 text-sm font-ui cursor-pointer transition-colors duration-fast',
										'flex items-center justify-between',
										'border-b border-border',
										'text-navy font-medium hover:bg-cream/60',
									)}
								>
									<span>
										{value.length === navigableOptions.length ? 'Deselect all' : 'Select all'}
									</span>
									<span className="text-xs text-muted-foreground">
										{navigableOptions.length} items
									</span>
								</li>
							)}

							{/* Search input */}
							{searchable && (
								<div className="p-2 border-b border-border sticky top-0 bg-card z-10">
									<input
										ref={search === '' && isOpen ? undefined : searchInputRef}
										type="text"
										className={cn(
											'w-full font-ui text-sm rounded-md border border-input px-2.5 py-1.5',
											'bg-background text-foreground placeholder:text-muted-foreground',
											'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
										)}
										placeholder="Type to search…"
										value={search}
										onChange={e => {
											setSearch(e.target.value)
											setActiveIndex(-1)
										}}
										onClick={e => e.stopPropagation()}
										onKeyDown={handleSearchKeyDown}
									/>
									{filteredOptions.length === 0 && (
										<p className="text-xs text-muted-foreground text-center py-2 font-ui">
											{emptyMessage}
										</p>
									)}
								</div>
							)}

							{/* Options */}
							<ul role="presentation">
								{Array.from(groups.entries()).map(([group, opts]) => (
									<React.Fragment key={group}>
										{group && (
											<li className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider font-ui bg-muted/50">
												{group}
											</li>
										)}
										{opts.map((opt, optIdx) => {
											const isSelected = selectedSet.has(opt.value)
											const flatIndex = navigableOptions.indexOf(opt)

											return (
												<motion.li
													key={opt.value}
													ref={(el: HTMLLIElement | null) => {
														if (flatIndex >= 0) optionElements.current[flatIndex] = el
													}}
													role="option"
													aria-selected={isSelected}
													aria-disabled={opt.disabled || undefined}
													onClick={() => handleSelect(opt)}
													onMouseEnter={() => setActiveIndex(flatIndex)}
													className={cn(
														'px-3 py-2 text-sm font-ui cursor-pointer transition-colors duration-fast',
														'select-none flex items-center gap-2',
														isSelected
															? 'bg-navy/10 text-navy-900 font-medium'
															: 'text-foreground hover:bg-cream/60',
														opt.disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
														flatIndex === activeIndex && !opt.disabled && 'bg-cream/80',
														isMaxed && !isSelected && !opt.disabled && 'opacity-50 cursor-not-allowed',
													)}
													initial={{ opacity: 0, x: -4 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{
														duration: 0.15,
														delay: optIdx * 0.02,
														ease: [0.4, 0, 0.2, 1],
													}}
												>
													{/* Checkbox indicator */}
													<span
														className={cn(
															'flex items-center justify-center w-4 h-4 rounded border-2 flex-shrink-0 transition-colors',
															isSelected
																? 'bg-navy border-navy text-cream-50'
																: 'border-input',
														)}
													>
														{isSelected && <CheckIcon size={10} />}
													</span>

													<span className="flex-1 truncate">
														{searchable ? (
															<HighlightedText text={opt.label} query={search} />
														) : (
															opt.label
														)}
													</span>
												</motion.li>
											)
										})}
									</React.Fragment>
								))}
							</ul>

							{/* Empty state (no options at all) */}
							{filteredOptions.length === 0 && !searchable && (
								<p className="text-xs text-muted-foreground text-center py-4 font-ui">
									{emptyMessage}
								</p>
							)}

							{/* Footer with selection count */}
							{value.length > 0 && (
								<div className="px-3 py-1.5 border-t border-border bg-muted/30 flex items-center justify-between">
									<span className="text-xs text-muted-foreground font-ui">
										{value.length} selected
									</span>
									<button
										type="button"
										onClick={e => {
											e.stopPropagation()
											handleClearAll()
										}}
										className="text-xs text-muted-foreground hover:text-foreground font-ui transition-colors"
									>
										Clear all
									</button>
								</div>
							)}
						</motion.div>
					</AnimatePresence>,
					document.body,
				)}
			</div>

			{error && (
				<motion.p
					id={`${selectId}-error`}
					className="text-xs text-error font-ui"
					initial={{ opacity: 0, y: -4 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.2 }}
				>
					{error}
				</motion.p>
			)}
			{hint && !error && (
				<p id={`${selectId}-hint`} className="text-xs text-muted-foreground font-ui">
					{hint}
				</p>
			)}
		</div>
	)
}
